// lib/push.ts — phone and desktop notifications on THIS device
// (server/src/push.js, web/public/sw.js).
//
// Turning on is always a person's tap: enablePush() is the only thing that
// asks the browser for permission, and only the Notifications section and the
// dashboard card call it, from a click. Nothing here runs on load.
//
//   pushSupport()        can this browser take notifications at all, and if
//                        not, what to tell the person (iPhone: only from the
//                        Home Screen app, iOS 16.4+)
//   enablePush(api)      permission → the service worker's push subscription
//                        → POST /push/subscribe. Marks this device on.
//   disablePush(api)     the reverse
//   syncPushDevice(api)  a device removed from another device's list (or
//                        dropped by the push service) is switched off here too
//   listenForPushClicks  a tapped notification with the app already open: the
//                        service worker asks this tab to go to the screen
//                        instead of opening a second one
//
// No imports: the pure half is unit-tested in node (scripts/unit-push.mjs).

export const ON_KEY = "fw_push_on";                       // "1" while this device is subscribed (per browser)
export const PROMPT_KEY = "fw_push_prompt_dismissed";     // the dashboard card's "Not now" (epoch ms)
export const PROMPT_QUIET_MS = 30 * 86400000;
export const COVERED_KINDS = ["lead", "signed", "payment"];   // in-app kinds a push already buzzes for

export type SupportState = "ready" | "ios-install" | "ios-old" | "unsupported" | "insecure" | "denied";
export type Support = { state: SupportState; ios: boolean; standalone: boolean; permission: string };
export type SupportEnv = {
  ua: string; standalone: boolean; secure: boolean; hasSW: boolean; hasPush: boolean; hasNotification: boolean;
  permission?: string; maxTouchPoints?: number;
};

/* ============================================================
   Pure
   ============================================================ */

/* iPhone / iPod / iPad — including iPadOS, which reports itself as a Mac but
   has a touch screen */
export function isIOS(ua: string, maxTouchPoints = 0): boolean {
  const s = String(ua || "");
  if (/iPhone|iPad|iPod/i.test(s)) return true;
  return /Macintosh/.test(s) && maxTouchPoints > 1;
}

/* [major, minor] from "iPhone OS 16_4" / "Version/16.4", or null */
export function iosVersion(ua: string): [number, number] | null {
  const s = String(ua || "");
  const m = /OS (\d+)[_.](\d+)/.exec(s) || /Version\/(\d+)\.(\d+)/.exec(s);
  return m ? [Number(m[1]), Number(m[2])] : null;
}

/* What this browser can do. iPhone first: Safari only delivers web push to an
   app added to the Home Screen (iOS 16.4+), and in a Safari tab the push API
   simply isn't there — so "unsupported" would be the wrong thing to say. */
export function pushSupport(env: SupportEnv): Support {
  const ios = isIOS(env.ua, env.maxTouchPoints || 0);
  const permission = env.permission || "default";
  const out = (state: SupportState): Support => ({ state, ios, standalone: !!env.standalone, permission });
  if (ios) {
    const v = iosVersion(env.ua);
    if (v && (v[0] < 16 || (v[0] === 16 && v[1] < 4))) return out("ios-old");
    if (!env.standalone) return out("ios-install");
  }
  if (!env.secure) return out("insecure");
  if (!env.hasSW || !env.hasPush || !env.hasNotification) return out("unsupported");
  if (permission === "denied") return out("denied");
  return out("ready");
}

/* "iPhone · Home Screen app", "Mac · Chrome" — for the device list */
export function deviceLabel(ua: string, standalone = false, maxTouchPoints = 0): string {
  const s = String(ua || "");
  const ipad = /iPad/.test(s) || (/Macintosh/.test(s) && maxTouchPoints > 1);
  const os = /iPhone/.test(s) ? "iPhone" : ipad ? "iPad" : /Android/.test(s) ? "Android" : /Macintosh|Mac OS X/.test(s) ? "Mac" : /Windows/.test(s) ? "Windows" : /CrOS/.test(s) ? "Chromebook" : /Linux/.test(s) ? "Linux" : "";
  const br = /Edg\//.test(s) ? "Edge" : /SamsungBrowser/.test(s) ? "Samsung Internet" : /Firefox\/|FxiOS/.test(s) ? "Firefox" : /OPR\//.test(s) ? "Opera" : /Chrome\/|CriOS/.test(s) ? "Chrome" : /Safari\//.test(s) ? "Safari" : "";
  const how = standalone ? "Home Screen app" : br;
  return [os, how].filter(Boolean).join(" · ") || "This browser";
}

/* the VAPID public key (base64url) → the bytes pushManager.subscribe wants */
export function urlBase64ToUint8Array(s: string): Uint8Array {
  const b64 = String(s || "").replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64 + "===".slice((b64.length + 3) % 4);
  const bin = atob(pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
const sameBytes = (a: any, b: Uint8Array) => {
  if (!a) return false;
  const x = new Uint8Array(a);
  if (x.length !== b.length) return false;
  for (let i = 0; i < x.length; i++) if (x[i] !== b[i]) return false;
  return true;
};

/* Has something happened that a notification would have been worth it for?
   A lead from the website, widget, AI front desk or a lead email, or a signed
   quote. The dashboard card waits for this — asking on day one, before
   anything has ever come in, is asking for nothing. */
const LEAD_SOURCES = ["widget", "website", "booking", "referral", "ai-call", "call", "parsed", "email", "print"];
export function hasFirstMoment(store: any): boolean {
  const s = store || {};
  const quotes = Array.isArray(s.quotes) ? s.quotes : [];
  if (quotes.some((q: any) => q && (q.status === "signed" || q.status === "paid" || (q.signature && q.signature.at)))) return true;
  if (quotes.some((q: any) => q && LEAD_SOURCES.includes(String(q.source || "")))) return true;
  const customers = Array.isArray(s.customers) ? s.customers : [];
  return customers.some((c: any) => c && LEAD_SOURCES.includes(String(c.source || (c.data && c.data.source) || "")));
}

/* Should the dashboard show "Get a buzz when leads come in"? */
export function showPromptCard({ store, support, config, subscribed, dismissedAt = 0, now = Date.now() }: {
  store: any; support: Support; config: any; subscribed: boolean; dismissedAt?: number; now?: number;
}): boolean {
  if (!config || !config.enabled || config.setup === false) return false;
  if (!Array.isArray(config.events) || !config.events.length) return false;     // crew: nothing buzzes for them
  if (subscribed) return false;
  if (dismissedAt && now - dismissedAt < PROMPT_QUIET_MS) return false;
  if (support.state !== "ready" && support.state !== "ios-install") return false;  // blocked / unsupported: the card can't help
  return hasFirstMoment(store);
}

/* "just now", "5 min ago", "3 h ago", "Sep 21" */
export function agoLabel(ts: any, now = Date.now()): string {
  const t = typeof ts === "number" ? ts : Date.parse(String(ts || ""));
  if (!Number.isFinite(t)) return "";
  const m = Math.floor((now - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return m + " min ago";
  if (m < 1440) return Math.floor(m / 60) + " h ago";
  if (m < 7 * 1440) return Math.floor(m / 1440) + " d ago";
  return new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* A device row's second line: when it last took a notification, or why not */
export function deviceStatus(d: any, now = Date.now()): string {
  const bits: string[] = [];
  if (d && d.failures > 0) bits.push(`${d.failures} send${d.failures === 1 ? "" : "s"} didn't go through`);
  if (d && d.last_ok_at) bits.push("last notification " + agoLabel(d.last_ok_at, now));
  else bits.push("added " + agoLabel(d && d.created_at, now));
  return bits.join(" · ");
}

/* ============================================================
   This browser
   ============================================================ */
const ls = {
  get(k: string) { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set(k: string, v: string) { try { localStorage.setItem(k, v); } catch (e) {} },
};
export const pushOnHere = () => ls.get(ON_KEY) === "1";
const setOn = (on: boolean) => ls.set(ON_KEY, on ? "1" : "0");
/* the in-app alert for a kind a push already buzzes this device for would be
   a second buzz for the same thing */
export const pushCovers = (kind: string) => pushOnHere() && COVERED_KINDS.includes(String(kind || ""));

export function readSupport(): Support {
  const w: any = typeof window !== "undefined" ? window : {};
  const nav: any = typeof navigator !== "undefined" ? navigator : {};
  let standalone = false;
  try { standalone = !!(w.matchMedia && w.matchMedia("(display-mode: standalone)").matches) || nav.standalone === true; } catch (e) {}
  const N = w.Notification;
  return pushSupport({
    ua: nav.userAgent || "", standalone, secure: w.isSecureContext !== false,
    hasSW: !!(nav.serviceWorker), hasPush: "PushManager" in w, hasNotification: !!N,
    permission: N ? N.permission : "default", maxTouchPoints: nav.maxTouchPoints || 0,
  });
}
export const thisDeviceLabel = () => {
  const nav: any = typeof navigator !== "undefined" ? navigator : {};
  return deviceLabel(nav.userAgent || "", readSupport().standalone, nav.maxTouchPoints || 0);
};

/* The service worker registration — registering /sw.js if this page load
   hasn't yet (it registers on load in production; a person tapping "Turn on"
   before that shouldn't have to wait). */
export async function getRegistration(register = true): Promise<any> {
  const sw: any = typeof navigator !== "undefined" && (navigator as any).serviceWorker;
  if (!sw) return null;
  try {
    let reg = await sw.getRegistration();
    if (!reg && register) reg = await sw.register("/sw.js");
    if (!reg) return null;
    if (!reg.active) {
      // wait for it to activate (first install), but not forever
      await Promise.race([sw.ready, new Promise((r) => setTimeout(r, 8000))]);
      reg = (await sw.getRegistration()) || reg;
    }
    return reg;
  } catch (e) { return null; }
}
export async function currentSubscription(): Promise<any> {
  const reg = await getRegistration(false);
  if (!reg || !reg.pushManager) return null;
  try { return await reg.pushManager.getSubscription(); } catch (e) { return null; }
}

/* sha-256 of the endpoint, first 16 hex — the server's endpointKey(); how the
   device list knows which row is "this device" without handing endpoints out */
export async function endpointKey(endpoint: string): Promise<string> {
  const c: any = (globalThis as any).crypto;
  if (!endpoint || !c || !c.subtle) return "";
  const d = await c.subtle.digest("SHA-256", new TextEncoder().encode(endpoint));
  return Array.from(new Uint8Array(d)).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 16);
}

export type EnableResult = { ok: boolean; reason?: string; message?: string; device?: any };

/* Turn notifications on for this device. Call it ONLY from a click: this is
   where the browser's permission prompt appears. */
/* `register: false` (the Vite dev server) never installs the worker on
   demand — main.tsx keeps it out of dev on purpose (its cache would hold dev
   module URLs); a dev tab with no worker gets a plain message instead. */
export async function enablePush(api: any, publicKey: string, { register = true }: { register?: boolean } = {}): Promise<EnableResult> {
  const N: any = typeof window !== "undefined" ? (window as any).Notification : null;
  const sup = readSupport();
  if (sup.state !== "ready") return { ok: false, reason: sup.state };
  if (!publicKey) return { ok: false, reason: "off", message: "Notifications aren't set up on the server yet." };
  let perm = N ? N.permission : "denied";
  if (perm !== "granted") {
    try { perm = await N.requestPermission(); } catch (e) { perm = "default"; }
  }
  if (perm !== "granted") return { ok: false, reason: perm === "denied" ? "denied" : "dismissed" };
  const reg = await getRegistration(register);
  if (!reg || !reg.pushManager) return { ok: false, reason: "no_worker", message: register ? "This browser couldn't start the app's background worker. Reload the page and try again." : "Notifications need the production build — the service worker isn't registered on the dev server." };
  const key = urlBase64ToUint8Array(publicKey);
  let sub: any = null;
  try {
    sub = await reg.pushManager.getSubscription();
    // subscribed under an older server key → it can never receive; start over
    if (sub && sub.options && sub.options.applicationServerKey && !sameBytes(sub.options.applicationServerKey, key)) { await sub.unsubscribe(); sub = null; }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  } catch (e: any) {
    return { ok: false, reason: "subscribe_failed", message: "The browser wouldn't set up notifications (" + String((e && e.message) || e).slice(0, 120) + ")." };
  }
  try {
    const r = await api.subscribe(sub.toJSON ? sub.toJSON() : sub, thisDeviceLabel());
    setOn(true);
    return { ok: true, device: r && r.device };
  } catch (e: any) {
    return { ok: false, reason: (e && e.code) || "server", message: (e && e.message) || "Couldn't save this device." };
  }
}

/* Turn notifications off for this device (the browser and the server). */
export async function disablePush(api: any): Promise<{ ok: boolean }> {
  const sub = await currentSubscription();
  setOn(false);
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch (e) {}
  try { await api.unsubscribe(endpoint); } catch (e) {}
  return { ok: true };
}

/* Is this device on, as far as the SERVER knows? A device removed from the
   list on another device (or dropped by the push service) is switched off
   here too, so the screen never says "on" for a phone that won't buzz.
   → { subscribed, key } */
export async function syncPushDevice(devices: any[]): Promise<{ subscribed: boolean; key: string }> {
  const sub = await currentSubscription();
  if (!sub) { if (pushOnHere()) setOn(false); return { subscribed: false, key: "" }; }
  const key = await endpointKey(sub.endpoint);
  const listed = (devices || []).some((d: any) => d && d.key === key);
  if (!listed) {
    try { await sub.unsubscribe(); } catch (e) {}
    setOn(false);
    return { subscribed: false, key: "" };
  }
  setOn(true);
  return { subscribed: true, key };
}

/* A notification tapped while the app is open: sw.js focuses this tab and
   posts { type: "fw:navigate", url }. Route in place — pushState + popstate,
   which is exactly what the app's router listens to — so nothing reloads and
   nothing typed is lost. Same-origin paths only. */
let listening = false;
export function listenForPushClicks(navigate?: (url: string) => void): void {
  const sw: any = typeof navigator !== "undefined" && (navigator as any).serviceWorker;
  if (listening || !sw || !sw.addEventListener) return;
  listening = true;
  sw.addEventListener("message", (e: any) => {
    const d = e && e.data;
    if (!d || d.type !== "fw:navigate" || typeof d.url !== "string") return;
    let u: URL;
    try { u = new URL(d.url, location.origin); } catch (x) { return; }
    if (u.origin !== location.origin) return;
    const path = u.pathname + u.search;
    if (navigate) { navigate(path); return; }
    try { history.pushState({}, "", path); window.dispatchEvent(new PopStateEvent("popstate")); } catch (x) { location.assign(path); }
  });
}
